import React from 'react';
import PropTypes from 'prop-types';
import { flowRight as compose } from 'lodash';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import cx from 'classnames';

// Style
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './Calendar.css';
import calendarStyles from 'react-big-calendar/lib/css/react-big-calendar.css';

// GraphQL
import { withQuery } from '../../hoc/withGraphql';
import getHostReservationsForCalendarQuery from './getHostReservationsForCalendar.graphql';
import Loader from '../../components/Loader';
import CurrencyConverter from '../../components/CurrencyConverter';

const localizer = momentLocalizer(moment);

class Calendar extends React.Component {

  static propTypes = {
    data: PropTypes.shape({
      loading: PropTypes.bool,
      getHostReservationsForCalendar: PropTypes.array,
      refetch: PropTypes.func,
    })
  };

  state = {
    currentView: 'month',
    currentDate: new Date(),
    selectedEvent: null,
    showModal: false,
    selectedStatus: 'paid',
    dropdownOpen: false
  };

  componentDidMount() {
    this.fetchReservations();
  }

  fetchReservations = () => {
    const { data: { refetch } } = this.props;
    const { currentDate, currentView, selectedStatus } = this.state;

    let startDate, endDate;

    if (currentView === 'month') {
      startDate = moment(currentDate).startOf('month').format('YYYY-MM-DD');
      endDate = moment(currentDate).endOf('month').format('YYYY-MM-DD');
    } else if (currentView === 'week') {
      startDate = moment(currentDate).startOf('week').format('YYYY-MM-DD');
      endDate = moment(currentDate).endOf('week').format('YYYY-MM-DD');
    } else if (currentView === 'day') {
      startDate = moment(currentDate).format('YYYY-MM-DD');
      endDate = moment(currentDate).format('YYYY-MM-DD');
    }

    if (refetch) {
      refetch({ startDate, endDate, statusFilter: selectedStatus });
    }
  };

  handleNavigate = (date) => {
    this.setState({ currentDate: date }, this.fetchReservations);
  };

  handleViewChange = (view) => {
    this.setState({ currentView: view }, this.fetchReservations);
  };

  handleSelectEvent = (event) => {
    this.setState({ selectedEvent: event, showModal: true });
  };

  handleCloseModal = () => {
    this.setState({ selectedEvent: null, showModal: false });
  };

  handleStatusChange = (value) => {
    this.setState({ selectedStatus: value, dropdownOpen: false }, this.fetchReservations);
  };

  toggleDropdown = () => {
    this.setState({ dropdownOpen: !this.state.dropdownOpen });
  };

  closeDropdown = () => {
    this.setState({ dropdownOpen: false });
  };

  getEventStyle = (event) => {
    const { paymentState, reservationState } = event.resource;
    let backgroundColor = '#3e9b0a'; // default green

    // Determine color based on status combination
    if (paymentState === 'completed' && reservationState === 'completed') {
      backgroundColor = '#3e9b0a'; // green - paid
    } else if (paymentState === 'pending' && reservationState === 'approved') {
      backgroundColor = '#4a90e2'; // blue - pre-accepted
    } else if (paymentState === 'pending' && reservationState === 'pending') {
      backgroundColor = '#f5a623'; // yellow - pending
    } else if (paymentState === 'pending' && reservationState === 'declined') {
      backgroundColor = '#d0021b'; // red - canceled
    }

    return {
      style: {
        backgroundColor,
        borderColor: backgroundColor,
        color: 'white'
      }
    };
  };

  formatReservationsToEvents = (reservations) => {
    if (!reservations) return [];

    return reservations.map(reservation => ({
      id: reservation.id,
      title: `${reservation.listData?.title || 'Reservation'} - ${reservation.guestData?.displayName || 'Guest'}`,
      start: new Date(reservation.checkIn),
      end: new Date(reservation.checkOut),
      resource: reservation
    }));
  };

  render() {
    const { data: { loading, getHostReservationsForCalendar } } = this.props;
    const { currentDate, currentView, selectedEvent, showModal, selectedStatus, dropdownOpen } = this.state;

    if (loading) {
      return <Loader type="text" />;
    }

    const events = this.formatReservationsToEvents(getHostReservationsForCalendar);

    const STATUS_OPTIONS = [
      { value: 'paid', label: 'Paid', color: '#3e9b0a' },
      { value: 'pending', label: 'Pending', color: '#f5a623' },
      { value: 'pre-accepted', label: 'Pre-Accepted', color: '#4a90e2' },
      { value: 'canceled', label: 'Canceled', color: '#d0021b' }
    ];

    const currentOption = STATUS_OPTIONS.find(opt => opt.value === selectedStatus);

    return (
      <div className={s.container}>
        <div className={s.header}>
          <div className={s.filterContainer}>
            <label className={s.filterLabel}>Filter by Status:</label>
            <div className={s.customSelectWrapper}>
              <div className={s.customSelect} onClick={this.toggleDropdown}>
                <div className={s.selectedOption}>
                  {currentOption && currentOption.color && (
                    <span className={s.colorDot} style={{ backgroundColor: currentOption.color }}></span>
                  )}
                  <span>{currentOption ? currentOption.label : 'Select Status'}</span>
                </div>
                <span className={s.arrow}>{dropdownOpen ? '▲' : '▼'}</span>
              </div>
              {dropdownOpen && (
                <div className={s.dropdownMenu}>
                  {STATUS_OPTIONS.map(option => (
                    <div
                      key={option.value}
                      className={cx(s.dropdownItem, { [s.active]: option.value === selectedStatus })}
                      onClick={() => this.handleStatusChange(option.value)}
                    >
                      {option.color && (
                        <span className={s.colorDot} style={{ backgroundColor: option.color }}></span>
                      )}
                      <span>{option.label}</span>
                    </div>
                  ))}
                </div>
              )}
              {dropdownOpen && <div className={s.overlay} onClick={this.closeDropdown}></div>}
            </div>
          </div>
        </div>
        <div className={s.content}>
          <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            date={currentDate}
            view={currentView}
            onNavigate={this.handleNavigate}
            onView={this.handleViewChange}
            onSelectEvent={this.handleSelectEvent}
            eventPropGetter={this.getEventStyle}
            style={{ height: 700 }}
          />
        </div>

        {showModal && selectedEvent && (
          <div className={s.modalOverlay} onClick={this.handleCloseModal}>
            <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={s.modalHeader}>
                <h2>Reservation Details</h2>
                <button className={s.closeButton} onClick={this.handleCloseModal}>×</button>
              </div>
              <div className={s.modalBody}>
                <div className={s.infoRow}>
                  <strong>Property:</strong>
                  <span>{selectedEvent.resource.listData?.title || 'N/A'}</span>
                </div>
                <div className={s.infoRow}>
                  <strong>Guest:</strong>
                  <span>{selectedEvent.resource.guestData?.displayName || 'N/A'}</span>
                </div>
                <div className={s.infoRow}>
                  <strong>Guest Phone:</strong>
                  <span>{selectedEvent.resource.guestData?.phoneNumber || 'N/A'}</span>
                </div>
                <div className={s.infoRow}>
                  <strong>Reservation ID:</strong>
                  <span>{selectedEvent.resource.id}</span>
                </div>
                <div className={s.infoRow}>
                  <strong>Check-in:</strong>
                  <span>{moment(selectedEvent.start).format('DD/MM/YYYY')}</span>
                </div>
                <div className={s.infoRow}>
                  <strong>Check-out:</strong>
                  <span>{moment(selectedEvent.end).format('DD/MM/YYYY')}</span>
                </div>
                <div className={s.infoRow}>
                  <strong>Number of Nights:</strong>
                  <span>{selectedEvent.resource.dayDifference}</span>
                </div>
                <div className={s.infoRow}>
                  <strong>Number of Guests:</strong>
                  <span>{selectedEvent.resource.guests ?? 'N/A'}</span>
                </div>
                <div className={s.infoRow}>
                  <strong>Total Price:</strong>
                  <span>
                    <CurrencyConverter
                      amount={selectedEvent.resource.total }
                      from={selectedEvent.resource.currency}
                    />
                  </span>
                </div>
                <div className={s.infoRow}>
                  <strong>Advance Amount:</strong>
                  <span>
                    <CurrencyConverter
                      amount={selectedEvent.resource.advanceDeposit }
                      from={selectedEvent.resource.currency}
                    />
                  </span>
                </div>
                {
                  selectedEvent.resource.confirmationCode && (<div className={s.infoRow}>
                    <strong>Confirmation Code:</strong>
                    <span>{selectedEvent.resource.confirmationCode || 'N/A'}</span>
                  </div>)
                }
                <div className={s.infoRow}>
                  <strong>Status:</strong>
                  <span>{selectedEvent.resource.reservationState || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

}

export default compose(
  withStyles(s, calendarStyles),
  withQuery(getHostReservationsForCalendarQuery, {
    name: 'data',
    options: () => ({
      variables: {
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
        statusFilter: 'paid'
      },
      fetchPolicy: 'network-only',
      ssr: false
    })
  })
)(Calendar);
